import React, { useCallback, useMemo } from "react";
import MapboxDraw, { MapboxDrawOptions } from "@mapbox/mapbox-gl-draw";
import { FeatureCollection } from "geojson";

import { selectLayerConfigByKey, selectVersionConfig } from "fond/api";
import { MultiLayerEditProvider } from "fond/draw/MultiLayerEdit/MultiLayerEditProvider";
import { useMultiLayerFeatures } from "fond/draw/MultiLayerEdit/useMultiLayerFeatures";
import { validateHub } from "fond/draw/validation/isgValidation";
import {
  DeleteButton,
  PointButton,
  PolygonSelectButton,
  RedoButton,
  SelectButton,
  SnappingButton,
  ToolbarConfig,
  TopologicalButton,
  UndoButton,
} from "fond/map/Toolbar";
import { featureCollection } from "fond/turf";
import { ISGLayerIds } from "fond/types";
import { useAppSelector } from "fond/utils/hooks";

import HubsDrawContent from "./HubsDrawContent";

const allToolConfig: ToolbarConfig = [
  {
    id: "mainActions",
    type: "mode",
    items: [
      <SelectButton key="select" />,
      <PolygonSelectButton key="selectPolygon" />,
      <PolygonSelectButton key="selectLasso" selectionMode="lasso" />,
    ],
  },
  {
    id: "drawTools",
    type: "mode",
    items: [<PointButton key="drawPoint" />],
  },
  {
    id: "miscTools",
    type: "action",
    items: [<SnappingButton key="snapping" />, <TopologicalButton key="topological" />],
  },
  {
    id: "historyTools",
    type: "action",
    items: [<UndoButton key="undo" />, <RedoButton key="redo" />, <DeleteButton key="delete" />],
  },
];

/**
 * This component is responsible for managing the drawing of ISG Hubs.
 */
const HubsDrawPanel: React.FC = () => {
  const versionId = useAppSelector((state) => state.project.versionId);
  const versionConfig = useAppSelector((state) => selectVersionConfig(state, versionId));

  const { features, isLoading } = useMultiLayerFeatures(versionId, [ISGLayerIds.hubs, ISGLayerIds.ugPoints]);

  /**
   * Hub editing overrides the default initialFeature loading within MultiLayerEditProvider since we
   * source the hubs from a different set of layers to where we save them.
   */
  const initialFeatures = useMemo<FeatureCollection>(() => {
    return featureCollection(features);
  }, [features]);

  /**
   * Get the sublayer configuration by ID from the root-configuration.
   */
  const getConfigById = useCallback(
    (configId: string) => {
      return versionConfig?.Data?.entities?.[configId];
    },
    [versionConfig]
  );

  /**
   * Get the UG Point features to snap to.
   */
  const snapGetFeatures = useCallback((m: mapboxgl.Map, draw: MapboxDraw) => {
    return draw.getAll().features.filter((f) => f.properties?.layerId === ISGLayerIds.ugPoints);
  }, []);

  /**
   * Get the hub layer config to access sublayers
   */
  const hubLayerConfig = useAppSelector((state) => selectLayerConfigByKey(state, { versionId, layerKey: ISGLayerIds.hubs }));

  /**
   * Get the default editing layer IDs (parent + all sublayers for hubs)
   */
  const defaultEditingLayerIds = useMemo(() => {
    const layerIds: string[] = [ISGLayerIds.hubs]; // Start with parent layer

    // Add all sublayer IDs to make them editable by default
    if (hubLayerConfig?.Children) {
      layerIds.push(...hubLayerConfig.Children);
    }

    return layerIds;
  }, [hubLayerConfig]);

  // Configure drawing options with layer-specific styles
  const defaultOptions: MapboxDrawOptions = useMemo(
    () => ({
      snap: true,
      snapOptions: {
        snapVertexPriorityDistance: 0.000025,
        snapGetFeatures,
      },
      editOptions: {
        allLayers: [
          {
            ID: ISGLayerIds.hubs,
            GeometryType: "Point",
            getFeatureProperties: (_, editingLayerId) => {
              // Default to T1_HUB
              let type = "T1_HUB";

              // Get config (could be layer or sublayer) from root-configuration if editingLayerId is provided
              if (editingLayerId) {
                const config = getConfigById(editingLayerId);

                // If it's a sublayer, use its Key to determine the type
                if (config?.Type === "SUBLAYER") {
                  type = config.FilterConfiguration?.ValueKey;
                }
              }

              return { layerId: ISGLayerIds.hubs, TYPE: type };
            },
            validate: validateHub,
            Attributes: {
              TYPE: { Type: "ENUM", Options: ["T1_HUB", "T2_HUB", "T2_MID_SPLICE_HUB", "T3_HUB"] },
            },
            PromptAttributesOnCreate: true,
            ShowSublayers: true,
          },
          {
            ID: ISGLayerIds.ugPoints,
            GeometryType: "Point",
            Hide: true, // ugPoints are not directly editable, so hide them in the toolbar but make them available for snapping and validation
          },
        ],
        editingLayerId: ISGLayerIds.hubs, // Default selected layer
        editingLayerIds: defaultEditingLayerIds, // Default editing layers (parent + all sublayers)
      },
    }),
    [getConfigById, snapGetFeatures, defaultEditingLayerIds]
  );

  if (isLoading) return null;

  return (
    <MultiLayerEditProvider versionId={versionId} options={defaultOptions} config={allToolConfig} initialFeatures={initialFeatures}>
      <HubsDrawContent />
    </MultiLayerEditProvider>
  );
};

export default HubsDrawPanel;
